import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useFrontendUsage from '../../hooks/useFrontendUsage';
import {
  buildSharedManagementUrl,
  daysUntilLegacyShutdown,
  LEGACY_FRONTEND_SHUTDOWN_LABEL,
  MANAGEMENT_DOMAIN_SWITCH_DATE,
  PUBLIC_PATHS,
  SHARED_MANAGEMENT_HOSTNAME,
  SHARED_MANAGEMENT_URL,
} from '../../util/frontendMigration';
import { resolveViewTitle } from '../../util/viewTitle';

// The notice is shown once per login. What gets stored is a fingerprint of the
// login that acknowledged it: a reload or a second tab carries the same token
// and stays quiet, while the next login produces a different one and the notice
// returns. Deliberately a fingerprint and not the token itself — logging out
// clears the token, and a copy of it must not stay behind in storage.
const ACK_STORAGE_KEY = 'legacyShutdownNoticeAck';

// FNV-1a over the token. Not a security measure, just a short stable id that
// cannot be turned back into the token.
const fingerprint = token => {
  if (!token) return '';
  let hash = 0x811c9dc5;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16);
};

const readAck = () => {
  try {
    return localStorage.getItem(ACK_STORAGE_KEY) || '';
  } catch (_e) {
    // Storage blocked — the notice then simply shows on every load.
    return '';
  }
};

const POINTS = [
  {
    Icon: EventBusyIcon,
    title: `Am ${LEGACY_FRONTEND_SHUTDOWN_LABEL} wird diese Oberfläche abgeschaltet`,
    description:
      'Danach ist sie nicht mehr erreichbar. Alles, was du hier tust, geht genauso im neuen Management – mit demselben Login und denselben Daten.',
  },
  {
    Icon: CampaignIcon,
    title: `Seit dem ${MANAGEMENT_DOMAIN_SWITCH_DATE} gibt es Updates nur noch für das neue Management`,
    description:
      'Neue Funktionen und Verbesserungen entstehen ausschließlich dort. Hier kommt nichts Neues mehr dazu.',
  },
  {
    Icon: BookmarkBorderIcon,
    title: `Deine Adresse ist ${SHARED_MANAGEMENT_HOSTNAME}`,
    description:
      'Die gewohnte Adresse führt bereits ins neue Management. Am besten gleich als Lesezeichen setzen und von dort aus arbeiten.',
  },
  {
    Icon: BuildCircleIcon,
    title: 'Bis zur Abschaltung werden hier nur noch gravierende Fehler behoben',
    description:
      'Damit der Tagesbetrieb sicher läuft. Alles andere passiert im neuen Management.',
  },
];

// Announces the shutdown of this legacy frontend. Unlike the earlier one-time
// introductions this comes back with every login, because it is a deadline and
// not a piece of news: whoever still works here has to know how long for.
export default function LegacyFrontendSunsetModal() {
  const theme = useTheme();
  const location = useLocation();
  const auth = useAuth();
  const { trackSwitchToNewFrontend } = useFrontendUsage();
  const [saving, setSaving] = useState(false);
  const [ack, setAck] = useState(readAck);

  // A new login means a new token, so the stored acknowledgement no longer
  // matches and the notice is due again.
  useEffect(() => {
    setAck(readAck());
  }, [auth.token]);

  const shouldShow = useMemo(
    () =>
      Boolean(
        auth.token &&
          auth.userPreferencesHydrated &&
          !auth.isCounterDevice &&
          !PUBLIC_PATHS.has(location.pathname) &&
          ack !== fingerprint(auth.token)
      ),
    [auth.token, auth.userPreferencesHydrated, auth.isCounterDevice, ack, location.pathname]
  );

  const daysLeft = daysUntilLegacyShutdown();

  // Remembers this login as informed and closes the dialog.
  const markSeen = () => {
    const current = fingerprint(auth.token);
    try {
      localStorage.setItem(ACK_STORAGE_KEY, current);
    } catch (_e) {
      // Storage blocked — the notice comes back on the next load, which is the
      // harmless direction to fail in.
    }
    setAck(current);
  };

  const handleAcknowledge = () => {
    markSeen();
  };

  const handleSwitchNow = async () => {
    setSaving(true);
    markSeen();
    const viewTitle = resolveViewTitle({
      pathname: location.pathname,
      menuItems: auth.menuItems,
    });
    try {
      await trackSwitchToNewFrontend({
        path: location.pathname,
        viewTitle: viewTitle || undefined,
        origin: 'sunset-dialog',
      });
    } catch (_e) {
      // Tracking must never block the switch.
    }
    try {
      window.location.assign(buildSharedManagementUrl(location.pathname));
    } catch (_e) {
      toast.error('Wechsel ins neue Management fehlgeschlagen');
      setSaving(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <Dialog
      open={shouldShow}
      onClose={() => {}}
      fullWidth
      maxWidth='sm'
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: alpha('#000000', 0.52),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          backgroundImage: t =>
            t.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${theme.palette.background.paper} 42%)`
              : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.45)} 0%, ${theme.palette.background.paper} 40%)`,
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.22 : 0.35
              ),
            }}
          >
            <EventBusyIcon sx={{ color: 'text.primary' }} />
          </Box>
          <Box>
            <Typography variant='h5' component='h1' sx={{ fontWeight: 700 }}>
              Dieses Management wird am {LEGACY_FRONTEND_SHUTDOWN_LABEL} abgeschaltet
            </Typography>
            {daysLeft > 0 && (
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                label={daysLeft === 1 ? 'Noch 1 Tag' : `Noch ${daysLeft} Tage`}
                sx={{ mt: 0.75, fontWeight: 600 }}
              />
            )}
          </Box>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5, maxWidth: 500 }}>
          Gearbeitet wird ab dann ausschließlich im neuen Management unter{' '}
          {SHARED_MANAGEMENT_HOSTNAME}. Wer jetzt wechselt, hat den Umstieg in Ruhe hinter sich.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        <Stack spacing={1.5}>
          {POINTS.map(({ Icon, title, description }) => (
            <Stack key={title} direction='row' spacing={1.5} alignItems='flex-start'>
              <Icon sx={{ mt: 0.25, fontSize: 22, color: 'text.secondary' }} />
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {description}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.03),
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            Das neue Management erreichst du hier
          </Typography>
          <Link
            href={SHARED_MANAGEMENT_URL}
            target='_blank'
            rel='noopener noreferrer'
            variant='body2'
            sx={{ fontWeight: 600, wordBreak: 'break-all' }}
          >
            {SHARED_MANAGEMENT_HOSTNAME}
          </Link>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
      >
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={handleSwitchNow}
          disabled={saving}
          startIcon={<AutoAwesomeIcon />}
          sx={{ width: '100%', px: 3, py: 1.25, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? 'Einen Moment…' : `Jetzt zu ${SHARED_MANAGEMENT_HOSTNAME} wechseln`}
        </Button>
        <Box sx={{ minHeight: 32, display: 'flex', justifyContent: 'center' }}>
          <Button
            color='inherit'
            size='small'
            onClick={handleAcknowledge}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 400,
              color: 'text.disabled',
              '&:hover': { color: 'text.secondary', backgroundColor: 'transparent' },
            }}
          >
            Verstanden – ich arbeite hier noch weiter
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
